import { useState, useCallback, useRef } from 'react';
import {
  MAX_SPAN,
  TOTAL_TILES,
  generateLayout,
  getScore,
  spanToStage,
  type TileLayout,
} from '@/pages/spatial-span-shinhan/gameConfig';
import {
  unlockAudio,
  playTileShowSound,
  playCorrectSound,
  playWrongSound,
  playStageSound,
} from '@/pages/spatial-span-shinhan/utils/sound';

export interface AttemptResult {
  span: number;
  isRetry: boolean;
  isSuccess: boolean;
  presentedSequence: number[];
  userSequence: number[];
  touchReactionTimes: number[];
  attemptDuration: number;
}

export interface GameData {
  gameType: string;
  performedAt: string;
  results: AttemptResult[];
  maxSuccessSpan: number;
  maxSpanIsFirstTry: boolean;
  score: number;
  totalDuration: number;
  isAborted: boolean;
}

// 신한 SOL 버전 플로우: 서비스 소개 → 두뇌풀기 안내 → 게임 소개 → 본게임 → 결과
export type ScreenType = 'intro' | 'warmup' | 'guide' | 'game' | 'result';
// 화면 연출 전용 상태 (게임 판정 로직과 무관하게 UI 피드백만 제어)
export type RoundFeedback = 'showing' | 'input' | 'correct' | 'wrong';

function generateSequence(length: number, tileCount: number): number[] {
  const indices = Array.from({ length: tileCount }, (_, i) => i);
  const sequence: number[] = [];
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(Math.random() * indices.length);
    sequence.push(indices[randomIndex]);
    indices.splice(randomIndex, 1);
  }
  return sequence;
}

export function useGameState() {
  const [screen, setScreen] = useState<ScreenType>('intro');
  const [currentSpan, setCurrentSpan] = useState(4);
  const [isRetry, setIsRetry] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [sequence, setSequence] = useState<number[]>([]);
  const [userSequence, setUserSequence] = useState<number[]>([]);
  const [isShowing, setIsShowing] = useState(false);
  const [isInputEnabled, setIsInputEnabled] = useState(false);
  const [results, setResults] = useState<AttemptResult[]>([]);
  const [maxSuccessSpan, setMaxSuccessSpan] = useState(0);
  const [maxSpanIsFirstTry, setMaxSpanIsFirstTry] = useState(false);
  const [score, setScore] = useState(0);
  const [layout, setLayout] = useState<TileLayout>([]);
  const [showingIndex, setShowingIndex] = useState(-1);
  const [showingStep, setShowingStep] = useState(-1);
  const [message, setMessage] = useState('');
  const [showExitModal, setShowExitModal] = useState(false);
  const [feedback, setFeedback] = useState<RoundFeedback>('showing');
  const [isFinalizing, setIsFinalizing] = useState(false);

  // 연출 전용: 정답 순서 Replay / Stage 성공 Pulse / 짧은 성공 텍스트
  const [isReplaying, setIsReplaying] = useState(false);
  const [replayIndex, setReplayIndex] = useState(-1);
  const [stagePulse, setStagePulse] = useState(false);
  const [stageText, setStageText] = useState('');

  const roundStartTimeRef = useRef<number>(0);
  const gameStartTimeRef = useRef<number>(0);
  const touchTimesRef = useRef<number[]>([]);
  const isAnimatingRef = useRef(false);

  // 비동기 타임아웃에서 안전하게 읽기 위한 ref (stale closure 방지)
  const attemptCountRef = useRef(0);
  const currentSpanRef = useRef(4);
  const isRetryRef = useRef(false);
  const resultsRef = useRef<AttemptResult[]>([]);
  const maxSuccessSpanRef = useRef(0);
  const maxSpanIsFirstTryRef = useRef(false);

  const setSpan = useCallback((span: number) => {
    currentSpanRef.current = span;
    setCurrentSpan(span);
  }, []);

  // 현재 span으로 점등 애니메이션 + 입력 대기를 시작
  const startAttempt = useCallback(() => {
    const span = currentSpanRef.current;
    const newSequence = generateSequence(span, TOTAL_TILES);
    setSequence(newSequence);
    setUserSequence([]);
    setIsShowing(true);
    setIsInputEnabled(false);
    setShowingIndex(-1);
    setShowingStep(-1);
    setReplayIndex(-1);
    setIsReplaying(false);
    setStageText('');
    setFeedback('showing');
    setMessage('불이 켜지는 순서를 기억해주세요');
    isAnimatingRef.current = true;
    roundStartTimeRef.current = Date.now();
    touchTimesRef.current = [];

    let currentIndex = 0;

    const showNext = () => {
      if (currentIndex >= newSequence.length) {
        setShowingIndex(-1);
        setShowingStep(-1);
        setIsShowing(false);
        setIsInputEnabled(true);
        setFeedback('input');
        setMessage('같은 순서대로 눌러주세요');
        isAnimatingRef.current = false;
        return;
      }
      setShowingIndex(newSequence[currentIndex]);
      setShowingStep(currentIndex);
      playTileShowSound();
      currentIndex++;
      setTimeout(() => {
        setShowingIndex(-1);
        setTimeout(showNext, 250);
      }, 600);
    };

    setTimeout(showNext, 600);
  }, []);

  // 정답 순서 Replay: 성공 보상용 연출 (학습 목적 아님)
  // - 입력 순서에 관계없이 정답 타일을 정답 순서대로 빠르게 한 번 점등한다.
  // - 문제 제시보다 빠르게(전체 약 0.5~0.8초) 재생하고, Replay 동안에는 입력을 받지 않는다.
  const runReplay = useCallback((correctSequence: number[], onDone: () => void) => {
    isAnimatingRef.current = true;
    setIsReplaying(true);
    setIsInputEnabled(false);

    const stepMs = 100;
    const gapMs = 30;
    let i = 0;

    const step = () => {
      if (i >= correctSequence.length) {
        setReplayIndex(-1);
        window.setTimeout(() => {
          setIsReplaying(false);
          isAnimatingRef.current = false;
          onDone();
        }, 90);
        return;
      }
      setReplayIndex(correctSequence[i]);
      playTileShowSound();
      i += 1;
      window.setTimeout(() => {
        setReplayIndex(-1);
        window.setTimeout(step, gapMs);
      }, stepMs);
    };

    window.setTimeout(step, 90);
  }, []);

  // Stage 성공 연출: Replay → 전체 Pulse → (마지막이면 완료 / 아니면 다음 Stage)
  const runStageSuccess = useCallback(
    (isLast: boolean, span: number, correctSequence: number[]) => {
      runReplay(correctSequence, () => {
        setStagePulse(true);
        playStageSound();
        if (!isLast) {
          setStageText('좋아요!');
        }
        window.setTimeout(() => setStagePulse(false), 420);

        if (isLast) {
          // 전체 게임 완료: Pulse 후 "완료!" 연출 → 결과 화면
          window.setTimeout(() => {
            setIsFinalizing(true);
            window.setTimeout(() => finishGame(), 420);
          }, 400);
          return;
        }

        // 다음 Stage: Progress 변경 → 다음 문제 제시 (별도 버튼/안내 화면 없음)
        window.setTimeout(() => {
          setStageText('');
          setSpan(Math.min(span + 1, MAX_SPAN));
          isRetryRef.current = false;
          setIsRetry(false);
          startAttempt();
        }, 460);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runReplay, setSpan, startAttempt],
  );

  const finishGame = useCallback((aborted = false) => {
    const totalDuration = Date.now() - gameStartTimeRef.current;
    const finalSpan = maxSuccessSpanRef.current;
    const isFirstTry = maxSpanIsFirstTryRef.current;
    const finalScore = getScore(finalSpan, isFirstTry);

    setScore(finalScore);
    setMaxSuccessSpan(finalSpan);
    setMaxSpanIsFirstTry(isFirstTry);
    setIsFinalizing(false);
    setIsReplaying(false);
    setReplayIndex(-1);
    setStagePulse(false);
    setStageText('');

    const gameData: GameData = {
      gameType: 'spatial-span',
      performedAt: new Date().toISOString(),
      results: resultsRef.current,
      maxSuccessSpan: finalSpan,
      maxSpanIsFirstTry: isFirstTry,
      score: finalScore,
      totalDuration,
      isAborted: aborted,
    };
    console.log('Game Data:', gameData);
    setScreen('result');
  }, []);

  // 게임 안내 → 본게임: 별도 Countdown/안내 화면 없이 Stage 1로 바로 진입
  const startGame = useCallback(() => {
    // 사용자 제스처 안에서 오디오 컨텍스트를 미리 깨워둔다
    unlockAudio();
    setScreen('game');
    attemptCountRef.current = 0;
    setAttemptCount(0);
    setSpan(4);
    isRetryRef.current = false;
    setIsRetry(false);
    resultsRef.current = [];
    setResults([]);
    maxSuccessSpanRef.current = 0;
    maxSpanIsFirstTryRef.current = false;
    setMaxSuccessSpan(0);
    setMaxSpanIsFirstTry(false);
    setIsFinalizing(false);
    setIsReplaying(false);
    setReplayIndex(-1);
    setStagePulse(false);
    setStageText('');
    setLayout(generateLayout());
    gameStartTimeRef.current = Date.now();
    startAttempt();
  }, [setSpan, startAttempt]);

  const goToGuide = useCallback(() => {
    setScreen('guide');
  }, []);

  const goToWarmup = useCallback(() => {
    setScreen('warmup');
  }, []);

  const goToIntro = useCallback(() => {
    setScreen('intro');
  }, []);

  const openExitModal = useCallback(() => {
    setShowExitModal(true);
  }, []);

  const closeExitModal = useCallback(() => {
    setShowExitModal(false);
  }, []);

  const confirmExit = useCallback(() => {
    setShowExitModal(false);
    setScreen('intro');
    setSequence([]);
    setUserSequence([]);
    setIsShowing(false);
    setIsInputEnabled(false);
    setResults([]);
    setSpan(4);
    setIsRetry(false);
    attemptCountRef.current = 0;
    setAttemptCount(0);
    isRetryRef.current = false;
    resultsRef.current = [];
    maxSuccessSpanRef.current = 0;
    maxSpanIsFirstTryRef.current = false;
    setMaxSuccessSpan(0);
    setMaxSpanIsFirstTry(false);
    setScore(0);
    setShowingIndex(-1);
    setShowingStep(-1);
    setMessage('');
    setFeedback('showing');
    setIsFinalizing(false);
    setIsReplaying(false);
    setReplayIndex(-1);
    setStagePulse(false);
    setStageText('');
    isAnimatingRef.current = false;
  }, [setSpan]);

  const handleGameTilePress = useCallback(
    (tileIndex: number) => {
      if (!isInputEnabled || isAnimatingRef.current || isShowing) return;

      const touchTime = Date.now() - roundStartTimeRef.current;
      touchTimesRef.current = [...touchTimesRef.current, touchTime];

      const newUserSequence = [...userSequence, tileIndex];
      setUserSequence(newUserSequence);

      if (newUserSequence.length === sequence.length) {
        const isCorrect = newUserSequence.every((val, idx) => val === sequence[idx]);
        const attemptDuration = Date.now() - roundStartTimeRef.current;
        setIsInputEnabled(false);

        const span = currentSpanRef.current;
        const retry = isRetryRef.current;

        const result: AttemptResult = {
          span,
          isRetry: retry,
          isSuccess: isCorrect,
          presentedSequence: [...sequence],
          userSequence: [...newUserSequence],
          touchReactionTimes: [...touchTimesRef.current],
          attemptDuration,
        };

        const newResults = [...resultsRef.current, result];
        resultsRef.current = newResults;
        setResults(newResults);

        if (isCorrect && span > maxSuccessSpanRef.current) {
          maxSuccessSpanRef.current = span;
          maxSpanIsFirstTryRef.current = !retry;
        }

        attemptCountRef.current += 1;

        // 스테이지 종료는 '시도 횟수'가 아니라 '마지막 스테이지 여부'로 판단한다 (총 2 스테이지)
        const isLastStage = span >= MAX_SPAN;

        if (isCorrect) {
          // 정답: 짧은 성공 피드백 → 곧바로 정답 순서 Replay → Stage 성공 연출
          setFeedback('correct');
          setMessage('정확해요!');
          playCorrectSound();
          window.setTimeout(() => {
            runStageSuccess(isLastStage, span, [...sequence]);
          }, 240);
          return;
        }

        // 오답: 명확하지만 부정적이지 않게 (Shake + 약한 색상 피드백, 실패 경험을 길게 끌지 않음)
        setFeedback('wrong');
        playWrongSound();

        if (!retry) {
          // 1차 실패 → 동일 난이도 1회 재수행
          isRetryRef.current = true;
          setIsRetry(true);
          setMessage('한 번 더 집중해볼까요?');
          window.setTimeout(() => {
            startAttempt();
          }, 1400);
          return;
        }

        // 재수행 실패 → 마지막 스테이지면 종료, 아니면 다음 난이도로 상승
        isRetryRef.current = false;
        setIsRetry(false);
        if (isLastStage) {
          setMessage('좋아요.');
          setIsFinalizing(true);
          window.setTimeout(() => {
            finishGame();
          }, 900);
          return;
        }
        setMessage('좋아요. 다음 단계로 넘어갈게요.');
        window.setTimeout(() => {
          setSpan(Math.min(span + 1, MAX_SPAN));
          startAttempt();
        }, 1400);
      }
    },
    [isInputEnabled, isShowing, userSequence, sequence, setSpan, startAttempt, finishGame, runStageSuccess],
  );

  const goToNext = useCallback(() => {
    // TODO: 기존 개발된 소음·환경 안내 페이지로 이동
  }, []);

  return {
    screen,
    currentSpan,
    stage: spanToStage(currentSpan),
    isRetry,
    attemptCount,
    sequence,
    userSequence,
    isShowing,
    isInputEnabled,
    results,
    maxSuccessSpan,
    maxSpanIsFirstTry,
    score,
    layout,
    showingIndex,
    showingStep,
    message,
    feedback,
    isFinalizing,
    isReplaying,
    replayIndex,
    stagePulse,
    stageText,
    startGame,
    goToGuide,
    goToWarmup,
    goToIntro,
    handleGameTilePress,
    goToNext,
    showExitModal,
    openExitModal,
    closeExitModal,
    confirmExit,
  };
}